import { createContext, use, useEffect, useRef, useState } from "react";
import { resolveAgencyKey } from "@/lib/agency";
import { useAnalytics } from "@/lib/analytics";
import {
  applyCanonicalRoutes,
  fetchCanonicalRoutes,
} from "@/lib/resolveFavoriteRoutes";

/** A saved Stop + Route + Direction triple representing a specific commute leg. */
export interface Favorite {
  /** UUID generated at save time. Used as the URL param and list key. */
  id: string;
  /** Agency Key owning this stop/route. Sent on the arrivals call. */
  agency: string;
  stopId: string;
  stopName: string;
  routeId: string;
  /** Short route identifier shown in the route badge (e.g. "720"). */
  shortName: string;
  /** Full route name, nullable. */
  longName: string | null;
  /** Direction-level headsign (e.g. "toward East LA"). Not per-trip tripHeadsign. */
  headsign: string;
  directionId: number;
  /**
   * Unix timestamp (ms) recorded at save time. Kept for analytics and merges;
   * display order is the stored array's order (the rider's order).
   */
  savedAt: number;
}

/** Which neighbour a Favorite swaps with when reordered. */
export type MoveDirection = "up" | "down";

const STORAGE_KEY = "via-la:favorites";

type Leg = Pick<Favorite, "stopId" | "routeId" | "directionId">;

/** Two Favorites are the same leg when stopId + routeId + directionId match. */
function isSameLeg(a: Leg, b: Leg): boolean {
  return (
    a.stopId === b.stopId &&
    a.routeId === b.routeId &&
    a.directionId === b.directionId
  );
}

function readFromStorage(): Favorite[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    // Favorites saved before the Agency Key existed have no `agency`, and ones
    // saved before the bus-feed rename store `lametro` (see docs/adr/0006).
    const parsed = JSON.parse(stored) as (Omit<Favorite, "agency"> & {
      agency?: string;
    })[];
    return parsed.map((f) => ({ ...f, agency: resolveAgencyKey(f.agency) }));
  } catch {
    return [];
  }
}

function writeToStorage(favorites: Favorite[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
  } catch {
    // localStorage unavailable (private mode / storage full)
  }
}

/**
 * How long surfaces wait for resolution before falling back to stored ids.
 * Only ends the wait: a later response is still applied.
 */
const RESOLVE_TIMEOUT_MS = 3000;

interface FavoritesContextValue {
  favorites: Favorite[];
  /** The rider has saved at least one Favorite: they open on the Favorites Home. */
  hasFavorites: boolean;
  addFavorite: (fav: Omit<Favorite, "id" | "savedAt">) => void;
  removeFavorite: (id: string) => void;
  /** Swaps a Favorite with its neighbour; no-op at either end. */
  moveFavorite: (id: string, direction: MoveDirection) => void;
  /** Puts a removed Favorite back at `index` (or last, if the list is now shorter). */
  restoreFavorite: (fav: Favorite, index: number) => void;
  isFavorited: (stopId: string, routeId: string, directionId: number) => boolean;
  /**
   * True while this load's route resolution is in flight. Surfaces that match
   * on routeId show their first-load skeleton instead of stale results.
   */
  resolving: boolean;
  /** True when this load's resolution found the Favorite's line no longer runs. */
  isDiscontinued: (id: string) => boolean;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

/**
 * Owns the user's saved Favorites in localStorage, shared app-wide from
 * `RootLayout` so every surface sees the same list.
 *
 * A Favorite is a Stop + Route + Direction triple. Uniqueness is determined
 * by the composite of stopId + routeId + directionId.
 *
 * Once per app load, stored routeIds are resolved to their Canonical Route Id
 * so Favorites saved before a Shakeup keep matching arrivals (docs/adr/0008).
 */
export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const [favorites, setFavorites] = useState<Favorite[]>(readFromStorage);
  const [resolving, setResolving] = useState(favorites.length > 0);
  // From the latest response only; never persisted (docs/adr/0008).
  const [discontinuedIds, setDiscontinuedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const resolveStarted = useRef(false);
  // The last committed list, read when the response lands so Favorites added
  // or removed while it was in flight are counted as the rider left them.
  const latestFavorites = useRef(favorites);
  const { capture } = useAnalytics();

  useEffect(() => {
    latestFavorites.current = favorites;
  }, [favorites]);

  useEffect(() => {
    // Once per app load; the ref also absorbs StrictMode's effect replay.
    if (resolveStarted.current) return;
    resolveStarted.current = true;
    if (latestFavorites.current.length === 0) return;

    // Deliberately not cleared on unmount: the provider lives for the app's
    // lifetime, and clearing it would strand StrictMode's replayed mount.
    setTimeout(() => {
      setResolving(false);
    }, RESOLVE_TIMEOUT_MS);

    fetchCanonicalRoutes(latestFavorites.current)
      .then((resolutions) => {
        const { changes, discontinuedIds } = applyCanonicalRoutes(
          latestFavorites.current,
          resolutions,
        );
        setDiscontinuedIds(new Set(discontinuedIds));
        setFavorites((prev) => {
          const next = applyCanonicalRoutes(prev, resolutions).favorites;
          writeToStorage(next);
          return next;
        });
        for (const change of changes) {
          capture("favorite_route_resolved", {
            from_route_id: change.fromRouteId,
            to_route_id: change.toRouteId,
            merged: change.merged,
          });
        }
      })
      .catch(() => {
        // Keep the stored ids, as before resolution existed; retried next load.
      })
      .finally(() => {
        setResolving(false);
      });
  }, [capture]);

  /** Appends a new Favorite. No-ops if the same stopId + routeId + directionId already exists. */
  const addFavorite = (fav: Omit<Favorite, "id" | "savedAt">) => {
    setFavorites((prev) => {
      const duplicate = prev.some((f) => isSameLeg(f, fav));
      if (duplicate) return prev;
      const next = [
        ...prev,
        { ...fav, id: crypto.randomUUID(), savedAt: Date.now() },
      ];
      writeToStorage(next);
      return next;
    });
  };

  /** Removes a Favorite by its UUID. */
  const removeFavorite = (id: string) => {
    setFavorites((prev) => {
      const next = prev.filter((f) => f.id !== id);
      writeToStorage(next);
      return next;
    });
  };

  const moveFavorite = (id: string, direction: MoveDirection) => {
    setFavorites((prev) => {
      const from = prev.findIndex((f) => f.id === id);
      const to = direction === "up" ? from - 1 : from + 1;
      if (from === -1 || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[from], next[to]] = [next[to], next[from]];
      writeToStorage(next);
      return next;
    });
  };

  /** No-ops if the Favorite, or another for the same leg, is already saved. */
  const restoreFavorite = (fav: Favorite, index: number) => {
    setFavorites((prev) => {
      const present = prev.some((f) => f.id === fav.id || isSameLeg(f, fav));
      if (present) return prev;
      const next = [...prev];
      next.splice(index, 0, fav);
      writeToStorage(next);
      return next;
    });
  };

  /** Returns true if the exact stopId + routeId + directionId combination is already saved. */
  const isFavorited = (
    stopId: string,
    routeId: string,
    directionId: number,
  ): boolean =>
    favorites.some((f) => isSameLeg(f, { stopId, routeId, directionId }));

  const isDiscontinued = (id: string): boolean => discontinuedIds.has(id);

  return (
    <FavoritesContext
      value={{
        favorites,
        hasFavorites: favorites.length > 0,
        addFavorite,
        removeFavorite,
        moveFavorite,
        restoreFavorite,
        isFavorited,
        resolving,
        isDiscontinued,
      }}
    >
      {children}
    </FavoritesContext>
  );
}

/**
 * Consumes the nearest {@link FavoritesProvider}.
 * Must be rendered inside one — throws if called outside the tree.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFavorites(): FavoritesContextValue {
  const ctx = use(FavoritesContext);
  if (!ctx) {
    throw new Error("useFavorites must be used inside <FavoritesProvider>");
  }
  return ctx;
}
